import { PdfBrandConfig } from '@common/types';
import { getCoverageLinesText } from '@common/utils';
import { getTimezoneLessDisplayDate } from '@common/utils/date-utils';
import { BoxTemplateLabels, FileType, PlacementType } from 'enums';
import { messages } from 'i18n';
import { PartialSubmission, Quote } from 'types';
import { getOneYearFromDate } from 'utils';
import {
  getRetailerFullName,
  getTodayDate,
} from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/components/formSteps/FlowQuote/pdf-creator/helpers';
import { InsightsData } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/form-methods/types';
import { getPdfBackCover } from 'broker/pages/SubmissionWorkspacePage/utils/pdf-creator/pdf-back-cover';
import { getPdfCover } from 'broker/pages/SubmissionWorkspacePage/utils/pdf-creator/pdf-cover';
import { usePDFCreateAndCombine } from 'broker/pages/SubmissionWorkspacePage/utils/pdf-creator/usePDFCreateAndCombine';
import { getPdfContent } from './pdf-content';

const {
  fileName,
  shortFileName,
  coverPage: coverPageTexts,
  nextStepsPage: nextStepsPageTexts,
} = messages.quoteComparisonPdf;

function getFileName(quotes: Quote[]) {
  const marketNames = quotes
    .map((quote) => quote.marketName?.toLowerCase())
    .filter((name): name is string => Boolean(name));
  return fileName(marketNames);
}

function getShortFileName(quotes: Quote[]) {
  return shortFileName(quotes.length);
}

function getMarketIds(quotes: Quote[]) {
  return quotes.map((quote) => quote.submissionMarketId).join(',');
}

function getCoverPage(submission: PartialSubmission, brandConfig: PdfBrandConfig) {
  const {
    title,
    deliveryDate,
    directTitle,
    directSubtitle1,
    directSubtitle2,
    directSubtitle2Fallback,
    directSubtitle3,
  } = coverPageTexts;
  if (submission.placementType !== PlacementType.Direct) {
    return getPdfCover({
      title: title(getRetailerFullName(submission.user)),
      subtitle1: deliveryDate(getTodayDate()),
      brandConfig,
    });
  }
  return getPdfCover({
    title: directTitle(submission.insuredName || ''),
    subtitle1: directSubtitle1(getCoverageLinesText(submission.coverageLines)),
    subtitle2: submission.dueDate
      ? directSubtitle2(
          getTimezoneLessDisplayDate(submission.dueDate),
          getTimezoneLessDisplayDate(getOneYearFromDate(submission.dueDate).toDate()),
        )
      : directSubtitle2Fallback,
    subtitle3: directSubtitle3(getTodayDate()),
    brandConfig,
  });
}

export function useGenerateAndUploadComparisonPDF() {
  const pdFCreateAndCombine = usePDFCreateAndCombine();

  async function generateAndUploadComparisonPDF(
    submission: PartialSubmission,
    quotes: Quote[],
    insights: InsightsData,
    brandConfig: PdfBrandConfig,
  ) {
    const marketIds = getMarketIds(quotes);
    const coverPage = await getCoverPage(submission, brandConfig);
    const nextStepsPage = await getPdfBackCover({
      headerContent: [],
      titleText: nextStepsPageTexts.title,
      subtitleText: nextStepsPageTexts.subtitle,
      brandConfig,
    });
    const content = getPdfContent({ submission, quotes, insights, agencyBrand: brandConfig.agencyBrand });

    return pdFCreateAndCombine(
      [
        {
          pdfContentProps: {
            ...coverPage,
            brandConfig,
          },
        },
        {
          pdfContentProps: {
            content,
            brandConfig,
          },
        },
        ...(submission.placementType !== PlacementType.Direct
          ? [
              {
                pdfContentProps: {
                  content: nextStepsPage,
                  brandConfig,
                },
              },
            ]
          : []),
      ],
      {
        parentFolderId: submission.boxFolderId,
        fileName: getFileName(quotes),
        alternativeShortFileName: getShortFileName(quotes),
        metadata: { [BoxTemplateLabels.FileType]: FileType.QuoteComparison, [BoxTemplateLabels.MarketId]: marketIds },
      },
    );
  }

  return generateAndUploadComparisonPDF;
}
